<template>
<!--
  他社からの乗り換えで、まとめて入れる画面。

  ## なぜ要るのか

  乗り換えてくる会社は、部署・取引先・社員・案件を1件ずつ手で
  作り直すことになる。**社員100人の会社なら、それだけで数日。**
  そこで折れると、製品の良し悪しの前に移行で止まる。

  ## 3つの段を守る

  ひな形を配る → 試す → 取り込む。

  **ひな形をこちらから配る。**「CSVで入れてください」とだけ言うと、
  列名も並びも各社の元システムのままになる。ひな形はサーバの
  列の定義から作っているので、列を足したときにずれない。

  **既定は試すだけ。**押し間違いで会社のマスタが増えるのは戻すのが
  大変なので、先に何行入るかと断る行を見せる。

  ## 入れる順番

  部署 → 取引先 → 社員 → 案件 → 有休残。依存で決まる。
  逆順に入れると参照先が無くて全部はねるので、画面もこの順に並べる。
-->
<div class="preika-route-view preika-route-view--form">
  <CCard>
    <CCardHeader>
      <div class="section-heading">
        <div>
          <strong>データの取り込み</strong>
          <p>
            他社からの乗り換えで、CSVからまとめて登録します。
            <b>入れる順番は 部署 → 取引先 → 社員 → 案件 → 有休残</b>です。
            先に入れたものを後のものが参照します。
          </p>
        </div>
        <router-link :to="{ name: '自社設定' }">
          <CButton color="secondary" variant="outline" size="sm">
            自社設定へ戻る
          </CButton>
        </router-link>
      </div>
    </CCardHeader>

    <CCardBody>
      <div v-if="loadError" class="preika-form-errors" role="alert">
        <p class="preika-form-message">{{ loadError }}</p>
      </div>

      <!-- 何を取り込むか -->
      <div class="import-kinds">
        <button
          v-for="kind in kinds"
          :key="kind.value"
          type="button"
          class="import-kind"
          :class="{ 'import-kind--on': kind.value === selected,
                    'import-kind--soon': !kind.ready }"
          :disabled="!kind.ready"
          @click="select(kind.value)">
          <span class="import-kind__name">{{ kind.label }}</span>
          <span v-if="!kind.ready" class="import-kind__soon">準備中</span>
        </button>
      </div>

      <template v-if="definition">
        <h3 class="import-section">1. ひな形を受け取る</h3>
        <p class="preika-form-hint">
          見出しの並びはこのひな形のとおりにしてください。
          <b>Excel で開けるように BOM 付きで配ります。</b>
        </p>
        <CButton color="primary" variant="outline" @click="downloadTemplate">
          {{ definition.title }}のひな形をダウンロード
        </CButton>

        <h3 class="import-section">2. 列の説明</h3>
        <div class="table-responsive">
          <table class="table table-bordered import-columns">
            <thead>
              <tr>
                <th>列</th>
                <th>要否</th>
                <th>説明</th>
                <th>例</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="column in definition.columns" :key="column.header">
                <td><strong>{{ column.header }}</strong></td>
                <td>
                  <span v-if="column.required" class="import-required">必須</span>
                  <span v-else>任意</span>
                </td>
                <td>{{ column.description }}</td>
                <td class="import-sample">{{ column.sample }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ul class="import-notes">
          <li v-for="(note, i) in definition.notes" :key="'note-' + i">{{ note }}</li>
        </ul>

        <h3 class="import-section">3. 貼り付けて試す</h3>
        <p class="preika-form-hint">
          CSV をそのまま貼り付けてください。
          <b>「試す」では何も書きません。</b>
        </p>
        <textarea
          v-model="csv"
          class="form-control import-csv"
          rows="10"
          :placeholder="definition.template"></textarea>

        <div class="import-actions">
          <CButton color="secondary" :disabled="busy || !csv.trim()" @click="run(false)">
            試す
          </CButton>
          <CButton
            color="primary"
            :disabled="busy || !canApply"
            @click="confirmAndRun">
            取り込む
          </CButton>
          <span v-if="busy" class="import-busy">実行中…</span>
        </div>

        <!-- 結果 -->
        <template v-if="result">
          <h3 class="import-section">結果</h3>
          <p :class="result.applied ? 'import-done' : 'import-dry'">
            <template v-if="result.applied">
              <b>{{ result.accepted }}件 取り込みました。</b>
            </template>
            <template v-else-if="result.rejected && result.rejected.length">
              <b>断った行があるので、1件も入れていません。</b>
              下の行を直してから、もう一度お試しください。
            </template>
            <template v-else>
              <b>{{ result.accepted }}件 入る予定です。</b>
              問題は見つかりませんでした。「取り込む」を押すと登録します。
            </template>
          </p>

          <div v-if="result.rejected && result.rejected.length"
               class="preika-form-errors" role="alert">
            <p class="preika-form-message"
               v-for="(row, i) in result.rejected" :key="'ng-' + i">
              {{ row.line }}行目: {{ row.reason }}
            </p>
          </div>
        </template>
      </template>
    </CCardBody>
  </CCard>
</div>
</template>

<script>
import axios from 'axios'

/**
 * 取り込める種類。**入れる順番に並べる。**
 *
 * ready が false のものは、まだサーバ側が受け付けない。
 * <b>先に並べておく。</b>「部署しか無い」画面を見た人は、
 * 社員を入れる方法が無いのだと思って手で作り始める。
 */
const KINDS = Object.freeze([
  { value: 'department', label: '部署', ready: true },
  { value: 'client', label: '取引先', ready: true },
  { value: 'user', label: '社員', ready: false },
  { value: 'project', label: '案件', ready: false },
  { value: 'paid-leave', label: '有休残', ready: false }
])

export default {
  name: 'DataImport',
  data() {
    return {
      kinds: KINDS,
      selected: 'department',
      definition: null,
      csv: '',
      result: null,
      busy: false,
      loadError: ''
    }
  },
  computed: {
    /**
     * 「取り込む」を押させてよいか。
     *
     * <b>試して、断る行が無いときだけ。</b>いきなり押せると、
     * 何行入るかを見ないまま会社のマスタが増える。
     */
    canApply() {
      return !!this.result
        && !this.result.applied
        && !(this.result.rejected && this.result.rejected.length)
        && this.result.accepted > 0
    }
  },
  created() {
    this.load()
  },
  methods: {
    select(kind) {
      if (kind === this.selected) { return }
      this.selected = kind
      this.csv = ''
      this.result = null
      this.load()
    },

    load() {
      this.loadError = ''
      this.definition = null
      return axios
        .get(`${process.env.VUE_APP_API_ORIGIN}/data-import/${this.selected}`,
          { withCredentials: true })
        .then(response => { this.definition = response.data })
        .catch(error => {
          this.loadError = this.messageOf(error,
            '取り込みの説明を読み込めませんでした。')
        })
    },

    /**
     * ひな形を受け取る。
     *
     * <b>リンクではなく、その場で組み立てて渡す。</b>
     * 認証が要る口なので、素の <a href> だとログイン画面のHTMLが
     * 落ちてくる。
     */
    downloadTemplate() {
      this.loadError = ''
      return axios
        .get(
          `${process.env.VUE_APP_API_ORIGIN}/data-import/${this.selected}/template`,
          { withCredentials: true, responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${this.selected}-template.csv`)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch(error => {
          this.loadError = this.messageOf(error,
            'ひな形を受け取れませんでした。')
        })
    },

    confirmAndRun() {
      const count = this.result ? this.result.accepted : 0
      if (!window.confirm(
        `${count}件を登録します。よろしいですか。`)) {
        return
      }
      this.run(true)
    },

    run(apply) {
      this.busy = true
      this.loadError = ''
      return axios
        .post(
          `${process.env.VUE_APP_API_ORIGIN}/data-import/${this.selected}`,
          { csv: this.csv, apply: apply },
          { withCredentials: true })
        .then(response => { this.result = response.data })
        .catch(error => {
          this.result = null
          this.loadError = this.messageOf(error, '取り込めませんでした。')
        })
        .finally(() => { this.busy = false })
    },

    /**
     * サーバが言っている理由を、そのまま出す。
     *
     * <b>「システムエラーです」で塗り潰さない。</b>断った理由は
     * 利用者が直せるものが多い（見出しが違う、行が長い、など）。
     */
    messageOf(error, fallback) {
      const body = error && error.response && error.response.data
      if (body && typeof body === 'object' && body.message) { return body.message }
      if (typeof body === 'string' && body) { return body }
      return fallback
    }
  }
}
</script>

<style scoped>
.import-kinds {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.import-kind {
  padding: 8px 16px;
  border: 1px solid var(--preika-blue-300, #b8c9e0);
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.import-kind--on {
  border-color: var(--preika-blue-600, #2f5d9e);
  background: var(--preika-blue-050, #f2f6fc);
  font-weight: 600;
}

.import-kind--soon {
  color: var(--preika-text-secondary, #6b7785);
  cursor: not-allowed;
}

.import-kind__soon {
  margin-left: 6px;
  font-size: 11px;
}

.import-section {
  margin: 24px 0 8px;
  font-size: 15px;
  font-weight: 600;
}

.import-columns td,
.import-columns th {
  font-size: 12px;
}

.import-required {
  color: var(--preika-danger-600, #c74855);
  font-weight: 600;
}

.import-sample {
  color: var(--preika-text-secondary, #6b7785);
}

.import-notes {
  margin: 8px 0 0;
  padding-left: 20px;
  font-size: 12px;
  line-height: 1.7;
}

.import-csv {
  font-family: Consolas, Menlo, monospace;
  font-size: 12px;
}

.import-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.import-busy {
  font-size: 12px;
  color: var(--preika-text-secondary, #6b7785);
}

.import-done {
  color: var(--preika-blue-700, #24487a);
}

.import-dry {
  color: var(--preika-text-secondary, #6b7785);
}
</style>
